// @ts-strict-ignore
import {BehaviorCountType} from 'common/query-builder/cohorts-query-builder/models/enums/expression-enums';
import {DatetimeOperator} from 'common/query-builder/cohorts-query-builder/models/enums/builder-enums';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeAttributes} from 'common/widgets/date-range-picker/models/types';
import {DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {
  SerializedBehaviorWindow,
  SerializedDatetime,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {Behavior} from 'common/query-builder/cohorts-query-builder/models/behaviors/behavior';

export const DatetimeOperatorToDateRangeTypeMapping: {[key in DatetimeOperator]: DateRangeType} = {
  [DatetimeOperator.Relative]: DateRangeType.RelativeAfter,
  [DatetimeOperator.Between]: DateRangeType.Between,
  [DatetimeOperator.On]: DateRangeType.On,
  [DatetimeOperator.After]: DateRangeType.Since,
  [DatetimeOperator.RelativeBeforeInPast]: DateRangeType.RelativeBefore,
  [DatetimeOperator.RelativeFuture]: DateRangeType.RelativeFuture,
};

export function convertSerializedDatetimeToDateRange(
  datetime: SerializedDatetime,
  dateTimeOperator: DatetimeOperator,
): DateRange {
  if (!(dateTimeOperator in DatetimeOperatorToDateRangeTypeMapping)) {
    // [shawn] This is never hit if compile time validations are correct but adding for runtime
    throw new Error(`Invalid datetime operator`);
  }

  let attrs: DateRangeAttributes = {
    type: DatetimeOperatorToDateRangeTypeMapping[dateTimeOperator],
  };

  switch (dateTimeOperator) {
    case DatetimeOperator.After:
    case DatetimeOperator.On:
      attrs.from = datetime.from_date ?? (datetime as string);
      break;
    case DatetimeOperator.Between:
      attrs = {
        ...attrs,
        from: datetime.from_date,
        to: datetime.to_date,
      };
      break;
    case DatetimeOperator.RelativeFuture:
    case DatetimeOperator.Relative:
      attrs.window = datetime.window;
      // historically future relative dates have used a negative value
      // since the date operator was not included, this changes with
      // the common DateRange UI
      attrs.window.value = Math.abs(attrs.window?.value);
      break;
    case DatetimeOperator.RelativeBeforeInPast:
      attrs.window = datetime.window || (datetime.to_date as SerializedBehaviorWindow);
      break;
    default:
      throw new Error(`Unsupported datetime operator conversion`);
  }

  return new DateRange(attrs);
}

export function getDateRangeFromBehavior(behavior: Behavior): DateRange {
  if (behavior.behaviorWindow) {
    return new DateRange({
      type: DateRangeType.RelativeAfter,
      window: behavior.behaviorWindow,
    });
  }

  let type = DateRangeType.Since;

  if (behavior.toDate === behavior.fromDate) {
    type = DateRangeType.On;
  } else if (behavior.toDate) {
    type = DateRangeType.Between;
  }

  return new DateRange({
    type,
    rolling: typeof behavior.fromDate !== `string`,
    from: behavior.fromDate,
    to: behavior.toDate,
  });
}

export function getDateRangeLabelFromBehavior(behavior: Behavior, negated: boolean): string {
  let connector = `from`;
  if (behavior.behaviorWindow) {
    connector = `in the`;
  } else if (behavior.fromDate === behavior.toDate) {
    connector = `on`;
  } else if (!behavior.toDate) {
    connector = `since`;
  }

  if (behavior.count?.type === BehaviorCountType.Day && !negated) {
    connector = `on unique days, ${connector}`;
  }

  return `${connector} ${behavior.getDateRangeLabel().toLowerCase()}`;
}
